import {
  base_colorContentBorder,
  base_colorContentBorderSecondary,
  text_colorTextPrimary,
  text_colorTextSecondary,
} from 'ext-ui/style/colors'
import { spacing } from 'ext-ui/style/spacing'
import {
  font_family_weight_medium,
  font_family_weight_regular,
  font_family_weight_semibold,
} from 'ext-ui/style/typography'
import { createGlobalStyle, css } from 'styled-components'

/** Date / description / amount. Shared so header, rows, and total stay aligned. */
const ledgerColumns = css`
  display: grid;
  grid-template-columns: 76px 1fr minmax(96px, max-content);
  gap: ${spacing(2)};
`

/**
 * The Modal renders our children inside a `.body` wrapper it owns, and the panel
 * is `display: flex` with `align-items: flex-start` — so `.body`, which sets only
 * `min-width: 0` and no width, shrink-wraps. The statement then sits in a narrow
 * column against the left edge instead of filling the panel's content box, which
 * the design fills at 600px. `.footer` escapes this only because the generated
 * style hands it `width: 100%`; `.body` gets nothing, and styling our own child
 * cannot help because the shrink happens one level above it.
 *
 * This is a Modal SPEC defect, not a Statements one: `.title`, `.description`,
 * and `.body` all shrink-wrap for every consumer in the app, and only `.footer`
 * was given a width. The real fix is `align-items: stretch` on `.panel` (or a
 * width on `.body`) in the design-system spec; until that lands this override
 * stays, and it should be deleted with it.
 *
 * `.body` is only reachable from outside via a class on the portal, and the
 * `css` prop does NOT work on a component here — this repo compiles
 * styled-components with `@swc/plugin-styled-components`, which has no css-prop
 * transform, so `<Modal css={…}>` silently emits no rule at all. A global style
 * keyed to a stable class is the pattern the legacy `Popup` already uses for
 * exactly this portal problem (`Popup.style.ts`).
 *
 * Interpolations are deliberately absent: `createGlobalStyle` evaluates them
 * eagerly at module load, which is the hazard documented on `PopupCardGlobalStyle`.
 */
export const STATEMENT_DETAIL_MODAL_CLASS = 'StatementDetailModal'

export const StatementDetailModalGlobalStyle = createGlobalStyle`
  .${STATEMENT_DETAIL_MODAL_CLASS} .body {
    width: 100%;
  }
`

/**
 * The design sets the statement body at 13px — between `font_size_sm` (12px) and
 * `font_size_md` (14px), with no token in between. Declared once here and shared
 * by the summary, ledger rows, and total so the three stay locked together, the
 * same documented-gap approach used for the 28px Payments figure.
 */
const statementBodyFontSize = '13px'
/** Design: 10.5px column headers. Nearest token, `font_size_xs`, is 10px. */
const ledgerHeaderFontSize = '10.5px'

export const summary = css`
  display: flex;
  flex-direction: column;
  gap: ${spacing(1)};
  margin-bottom: ${spacing(6)};
`

export const summaryRow = css`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${spacing(4)};
  padding: ${spacing(1.5)} 0;
  border-bottom: 1px solid ${base_colorContentBorderSecondary};
  font-size: ${statementBodyFontSize};
  font-family: ${font_family_weight_regular};
`

export const summaryLabel = css`
  color: ${text_colorTextSecondary};
`

export const summaryValue = css`
  color: ${text_colorTextPrimary};
  font-family: ${font_family_weight_medium};
  font-variant-numeric: tabular-nums;
`

export const ledgerHeader = css`
  ${ledgerColumns};
  padding: 0 ${spacing(1)} ${spacing(2)};
  border-bottom: 1px solid ${base_colorContentBorder};
`

export const ledgerHeaderCell = css`
  font-size: ${ledgerHeaderFontSize};
  font-family: ${font_family_weight_semibold};
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${text_colorTextSecondary};
`

export const ledgerHeaderAmountCell = css`
  ${ledgerHeaderCell};
  text-align: right;
`

/**
 * The one scroll owner in the modal. The summary above, the column header, and
 * the total below stay pinned while only the rows scroll — a design guardrail,
 * so do not move the overflow onto an ancestor.
 */
export const ledgerScroll = css`
  max-height: 320px;
  overflow-y: auto;
`

export const ledgerRow = css`
  ${ledgerColumns};
  padding: ${spacing(2)} ${spacing(1)};
  border-bottom: 1px solid ${base_colorContentBorderSecondary};
  font-size: ${statementBodyFontSize};
  font-family: ${font_family_weight_regular};
`

export const ledgerDate = css`
  color: ${text_colorTextSecondary};
`

export const ledgerDescription = css`
  color: ${text_colorTextPrimary};
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

export const ledgerAmount = css`
  color: ${text_colorTextPrimary};
  font-variant-numeric: tabular-nums;
  text-align: right;
`

export const ledgerTotal = css`
  ${ledgerColumns};
  padding: ${spacing(2.5)} ${spacing(1)} 0;
  margin-top: ${spacing(1)};
  border-top: 1px solid ${base_colorContentBorder};
  font-size: ${statementBodyFontSize};
  font-family: ${font_family_weight_medium};
  color: ${text_colorTextPrimary};
`

export const footer = css`
  display: flex;
  justify-content: flex-end;
  width: 100%;
`
